/**
 * What a character wears on their head and carries: headphones and a notebook, each in a few
 * models the person picks in the character builder.
 *
 * The same units as Figures.tsx: the head is centred at (0, -100) with a radius of about 23, the
 * ears at (±22.5, -98), and a figure holds things at chest height, hands at (±14, -50).
 */

import type { HeadphoneModel, LaptopModel } from './wardrobe'

/** The outline colour, the same ink as every figure. */
const INK = '#1a1f2c'

/* -------------------------------------------------------------- headphones */

/** Drawn over the face and hair, under whatever hat an occasion puts on top. */
export function Headphones({ model }: { model: HeadphoneModel }) {
  switch (model) {
    case 'headset':
      return (
        <g>
          <Band d="M -24 -104 C -26 -137 26 -137 24 -104" color="#3B4658" width={3} />
          <rect x="-30" y="-110" width="9" height="17" rx="4.5" fill="#3B4658" stroke={INK} strokeWidth="1.4" />
          <rect x="21" y="-110" width="9" height="17" rx="4.5" fill="#3B4658" stroke={INK} strokeWidth="1.4" />
          <rect x="-28.6" y="-105" width="2.2" height="7" rx="1.1" fill="#5CE1A6" />
          <Band d="M -25.5 -95 Q -22 -84 -11 -84" color="#3B4658" width={1.6} />
          <circle cx="-10" cy="-84" r="2.2" fill="#3B4658" stroke={INK} strokeWidth="1" />
        </g>
      )

    case 'studio':
      // Big padded cups and a thick band: the ones worn in a recording booth.
      return (
        <g>
          <Band d="M -25 -103 C -27 -140 27 -140 25 -103" color="#E4572E" width={5} />
          <path d="M -11 -130.5 Q 0 -133.5 11 -130.5" fill="none" stroke="#FFB199" strokeWidth="1.5" strokeLinecap="round" />
          {[-1, 1].map((side) => (
            <g key={side}>
              <ellipse cx={26 * side} cy="-100" rx="7.5" ry="11" fill="#2B2F38" stroke={INK} strokeWidth="1.5" />
              <ellipse cx={27 * side} cy="-100" rx="4.4" ry="7.4" fill="#E4572E" />
            </g>
          ))}
        </g>
      )

    case 'gamer':
      // Angular cups with a glowing ring each, and a boom microphone with a live light.
      return (
        <g>
          <Band d="M -24 -104 C -26 -138 26 -138 24 -104" color="#23262F" width={3.6} />
          <path d="M -17 -124 C -8 -132 8 -132 17 -124" fill="none" stroke="#22B8CF" strokeWidth="1" strokeLinecap="round" />
          <rect x="-31.5" y="-111" width="10.5" height="20" rx="3" fill="#1C1F26" stroke={INK} strokeWidth="1.5" />
          <rect x="21" y="-111" width="10.5" height="20" rx="3" fill="#1C1F26" stroke={INK} strokeWidth="1.5" />
          <rect x="-29.8" y="-108" width="7" height="14" rx="2" fill="none" stroke="#E64980" strokeWidth="1.5" />
          <rect x="22.8" y="-108" width="7" height="14" rx="2" fill="none" stroke="#22B8CF" strokeWidth="1.5" />
          <Band d="M -26 -92 Q -23 -81 -11 -82" color="#23262F" width={1.8} />
          <circle cx="-10" cy="-82" r="2.3" fill="#FA5252" stroke={INK} strokeWidth="1" />
        </g>
      )

    case 'earbuds':
      // Nothing over the head: a bud in each ear, with its short stem.
      return (
        <g>
          {[-1, 1].map((side) => (
            <g key={side}>
              <Band d={`M ${24 * side} -95 L ${23.4 * side} -88.5`} color="#F8F9FA" width={1.8} />
              <circle cx={24.5 * side} cy="-97" r="3.1" fill="#F8F9FA" stroke={INK} strokeWidth="1.1" />
            </g>
          ))}
        </g>
      )

    case 'retro':
      // A thin metal band and orange foam pads, as a cassette player came with.
      return (
        <g>
          <Band d="M -24 -103 C -25 -136 25 -136 24 -103" color="#AEB6BF" width={1.8} />
          {[-1, 1].map((side) => (
            <g key={side}>
              <circle cx={25 * side} cy="-99" r="6.4" fill="#F08C00" stroke={INK} strokeWidth="1.3" />
              <circle cx={25.6 * side} cy="-99" r="2.6" fill="#D9480F" opacity="0.55" />
            </g>
          ))}
        </g>
      )

    case 'cat':
      // Pastel, with a cat's ears on the band.
      return (
        <g>
          <path d="M -20 -121 L -16 -137 L -8 -127 Z" fill="#F7A8C0" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M -17.3 -124.5 L -15.6 -132 L -11.4 -126.8 Z" fill="#FFE3EC" />
          <path d="M 20 -121 L 16 -137 L 8 -127 Z" fill="#F7A8C0" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M 17.3 -124.5 L 15.6 -132 L 11.4 -126.8 Z" fill="#FFE3EC" />
          <Band d="M -24 -104 C -26 -137 26 -137 24 -104" color="#F7A8C0" width={3.6} />
          {[-1, 1].map((side) => (
            <g key={side}>
              <rect x={side < 0 ? -30 : 21} y="-110" width="9" height="17" rx="4.5" fill="#F7A8C0" stroke={INK} strokeWidth="1.4" />
              <circle cx={25.5 * side} cy="-101.5" r="1.4" fill="#FFFFFF" />
            </g>
          ))}
        </g>
      )

    case 'neckband':
      // Off the ears, resting round the neck: the band is behind it, the cups show under the jaw.
      return (
        <g>
          {[-1, 1].map((side) => (
            <g key={side} transform={`rotate(${-18 * side} ${15 * side} -75)`}>
              <ellipse cx={15 * side} cy="-75" rx="5.6" ry="6.6" fill="#343A40" stroke={INK} strokeWidth="1.3" />
              <ellipse cx={15.4 * side} cy="-75" rx="2.8" ry="3.8" fill="#4DABF7" />
            </g>
          ))}
        </g>
      )

    default:
      return null
  }
}

/** A band or a cable: the same path twice, outline under colour, as a figure's limbs are drawn. */
function Band({ d, color, width }: { d: string; color: string; width: number }) {
  return (
    <>
      <path d={d} fill="none" stroke={INK} strokeWidth={width + 2.6} strokeLinecap="round" />
      <path d={d} fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" />
    </>
  )
}

/* ---------------------------------------------------------------- notebooks */

const LIDS: Record<Exclude<LaptopModel, 'tablet'>, { lid: string; base: string }> = {
  graphite: { lid: '#4A5263', base: '#394150' },
  silver: { lid: '#CED4DA', base: '#ADB5BD' },
  gamer: { lid: '#1C1F26', base: '#111317' },
  classic: { lid: '#25282D', base: '#1A1C20' },
  white: { lid: '#F1F3F5', base: '#DEE2E6' },
  stickers: { lid: '#3B4658', base: '#2B3444' },
}

/**
 * A notebook held open at the chest, as a figure carries it while their agent works.
 *
 * The screen faces the person, so what the room sees is the lid — which is where a notebook shows
 * which one it is. A glow along the top edge says the screen is on. The tablet is the one model
 * seen from the front, as it always was: a screen with code on it.
 */
export function HeldLaptop({ model }: { model: LaptopModel }) {
  if (model === 'tablet') {
    return (
      <g>
        <rect x="-15" y="-63" width="30" height="20" rx="3" fill="#232C3D" stroke={INK} strokeWidth="1.5" />
        <rect x="-12.5" y="-60.5" width="25" height="15" rx="1.5" fill="#1E3A5F" />
        <g className="bh-code" stroke="#63D2FF" strokeWidth="1.2" strokeLinecap="round">
          <line x1="-9.5" y1="-57" x2="2" y2="-57" />
          <line x1="-9.5" y1="-53" x2="-2" y2="-53" />
          <line x1="-9.5" y1="-49" x2="6" y2="-49" />
        </g>
      </g>
    )
  }

  const colors = LIDS[model]
  return (
    <g>
      <path d="M -14 -66.8 L 14 -66.8" stroke="#8CE0FF" strokeWidth="1.6" strokeLinecap="round" opacity="0.6" />
      <g transform="translate(0 -55.5)">
        <rect x="-17" y="-10.5" width="34" height="21" rx="2.5" fill={colors.lid} stroke={INK} strokeWidth="1.5" />
        <LidArt model={model} />
      </g>
      <path d="M -18.5 -45 L 18.5 -45 L 16.5 -41.5 L -16.5 -41.5 Z" fill={colors.base} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
    </g>
  )
}

/**
 * The same notebook open on a desk, in front of a seated figure — in place of the keyboard, which
 * is what a notebook is for. The tablet is carried, not set down, so it draws nothing here.
 */
export function DeskLaptop({ model }: { model: LaptopModel }) {
  if (model === 'tablet') return null

  const colors = LIDS[model]
  return (
    <g>
      <g transform="translate(-3 -39.6) scale(0.82)">
        <rect x="-17" y="-10.5" width="34" height="21" rx="2.5" fill={colors.lid} stroke={INK} strokeWidth="1.6" />
        <LidArt model={model} />
      </g>
      <path d="M -19 -30.8 L 13 -30.8 L 15 -28.4 L -21 -28.4 Z" fill={colors.base} stroke={INK} strokeWidth="1" strokeLinejoin="round" />
    </g>
  )
}

/** What is on each lid, around its centre: a lid is 34 by 21. */
function LidArt({ model }: { model: Exclude<LaptopModel, 'tablet'> }) {
  switch (model) {
    case 'graphite':
      return <circle r="2.4" fill="#AEB6C2" />
    case 'silver':
      return (
        <>
          <rect x="-15" y="-8.8" width="30" height="5" rx="2" fill="#FFFFFF" opacity="0.35" />
          <circle r="2.4" fill="#F8F9FA" stroke="#ADB5BD" strokeWidth="0.6" />
        </>
      )
    case 'gamer':
      return (
        <>
          <path d="M -16 6 L -6 -9.5" stroke="#E64980" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M 6 9.5 L 16 -6" stroke="#22B8CF" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M 0 -3.6 L 3.6 2.6 L -3.6 2.6 Z" fill="#FA5252" />
        </>
      )
    case 'classic':
      return (
        <>
          <rect x="-13" y="-7.6" width="8" height="1.6" rx="0.8" fill="#6C737D" />
          <circle cx="11.5" cy="6" r="1.7" fill="#E03131" />
        </>
      )
    case 'white':
      return <circle r="2.4" fill="#CED4DA" />
    case 'stickers':
      return (
        <>
          <circle cx="-9" cy="-3.5" r="3.4" fill="#FFD43B" stroke={INK} strokeWidth="0.6" />
          <rect x="2.5" y="-7.5" width="8.5" height="5.2" rx="1.2" fill="#FF6B6B" stroke={INK} strokeWidth="0.6" transform="rotate(12 6.7 -4.9)" />
          <path d="M 9 1.5 L 12 3.2 L 12 6.6 L 9 8.3 L 6 6.6 L 6 3.2 Z" fill="#51CF66" stroke={INK} strokeWidth="0.6" />
          <circle cx="-3.5" cy="5.5" r="2.2" fill="#4DABF7" stroke={INK} strokeWidth="0.6" />
        </>
      )
  }
}
